import React, { useState, useEffect, useRef, useMemo } from "react";
import BannerSection from "./sections/BannerSection";
import LatestSection from "./sections/LatestSection";
import TopPicksSection from "./sections/TopPicksSection";
import ErrorState from "./states/ErrorState";
import LoadingSkeleton from "./states/LoadingSkeleton";
import useStore from "../../../store/useStore";
import { getGroupedLatestBannerContent, getLatest8Contents } from "../service";
import useContentData from "../hooks/useContentData";
import { useNavigate } from "react-router-dom";
import { markFeatureEntry, ZYMED_SOURCE } from "../../../utils/zymedAnalytics";

// Constants
const FILTER_DEBOUNCE_MS = 1000; // 500 milliseconds debounce for API calls

interface ForYouSectionProps {
  contentTypeHandler: (tab: string) => void;
}

// Function to transform grouped API response to desired format
const transformGroupedResponse = (response: any) => {
  if (!response?.grouped?.consolidatedModuleId?.groups) {
    return [];
  }

  const transformedData = response.grouped.consolidatedModuleId.groups.map((group: any) => {
    const doc = group.doclist.docs[0]; // Get the first document from each group
    return {
      id: doc.id,
      docId: doc.docId,
      contentTitle: doc.contentTitle,
      contentType: Array.isArray(doc.contentFormat) ? doc.contentFormat[0] || "documents" : doc.contentFormat || "documents", // Use contentFormat instead of contentType
      contentTypeId: doc.contentTypeId,
      contentDescription: doc.contentDescription,
      uploadThumbnailUrl: doc.uploadThumbnailUrl,
      coverPhotoUrl: "",
      duration: doc.duration,
      isLike: false,
      isBookmarked: false,
      viewCount: doc.view_count || 0,
      like_count: doc.like_count || 0,
      shareCount: doc.share_count || 0,
      contentGenreId: doc.contentGenreId || [],
      genres: [],
      publishedDate: doc.publishedDate,
      deepLinkCode: doc.deepLinkCode,
      consolidatedModule: doc.consolidatedModule || "",
    };
  });

  return transformedData;
};

// Function to transform latest content API response to desired format
const transformLatestContentResponse = (response: any) => {
  if (!response?.response?.docs) {
    return [];
  }

  const transformedData = response.response.docs.map((doc: any) => {
    return {
      id: doc.id,
      docId: doc.docId,
      contentTitle: doc.contentTitle,
      contentType: Array.isArray(doc.contentFormat) ? doc.contentFormat[0] || "documents" : doc.contentFormat || "documents", // Use contentFormat instead of contentType
      contentTypeId: doc.contentTypeId,
      contentDescription: doc.contentDescription,
      uploadThumbnailUrl: doc.uploadThumbnailUrl,
      coverPhotoUrl: "",
      duration: doc.duration,
      isLike: false,
      isBookmarked: false,
      viewCount: doc.view_count || 0,
      like_count: doc.like_count || 0,
      shareCount: doc.share_count || 0,
      contentGenreId: doc.contentGenreId || [],
      genres: [],
      publishedDate: doc.publishedDate,
      deepLinkCode: doc.deepLinkCode,
      consolidatedModule: doc.consolidatedModule || "",
    };
  });

  return transformedData;
};

const ForYouSection: React.FC<ForYouSectionProps> = ({
  contentTypeHandler,
}) => {
  const navigate = useNavigate(); 
  // Get store values
  const { pediaUserPreferences } = useStore();

  // Add mounted ref to prevent API calls after unmount
  const isMounted = useRef(true);

  // Content type tab state
  const [activeTopPickTab, setActiveTopPickTab] = useState<string>("");
  
  // State for grouped banner content
  const [groupedBannerData, setGroupedBannerData] = useState<any>(null);
  const [transformedBannerData, setTransformedBannerData] = useState<any[]>([]);
  const [groupedBannerLoading, setGroupedBannerLoading] = useState<boolean>(false);
  const [groupedBannerError, setGroupedBannerError] = useState<string | null>(null);

  // State for latest content
  const [latestContentData, setLatestContentData] = useState<any>(null);
  const [transformedLatestData, setTransformedLatestData] = useState<any[]>([]);
  const [latestContentLoading, setLatestContentLoading] = useState<boolean>(false);
  const [latestContentError, setLatestContentError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Get data with our custom hook
  const {
    bannerItems,
    latestItems,
    topPicksItems,
    topPicksAvailableTypes,
    contentLoading,
    contentError,
    debouncedApiCall,
  } = useContentData(FILTER_DEBOUNCE_MS);

  // Simple card click handler
  const handleCardClick = (index: number) => {
    if (transformedBannerData[index]) {
      if (window.location.pathname === "/") {
        markFeatureEntry("/content", ZYMED_SOURCE.HOME_CARD_CLICK);
      }
      navigate("/content", {
        state: {
          id: transformedBannerData[index].docId,
          date: transformedBannerData[index].publishedDate,
          previousTab: "latest",
          source: window.location.pathname + window.location.search,
        },
      });
    }
  };

  // Extract speciality IDs from user preferences with useMemo to prevent unnecessary re-renders
  const specialityIds = useMemo(() => {
    const extractSpecialityIds = (specialitiesString: string) => {
      if (!specialitiesString) return [];
      
      // Extract the content within parentheses
      const match = specialitiesString.match(/speciality_id:\((.*?)\)/);
      if (!match || !match[1]) return [];
      
      // Split by "OR" and trim to get clean IDs
      return match[1].split("OR").map((id) => parseInt(id.trim()));
    };

    const ids = extractSpecialityIds(pediaUserPreferences?.specialities);

    return ids;
  }, [pediaUserPreferences?.specialities]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Call getGroupedLatestBannerContent API
  useEffect(() => {
    const fetchGroupedBannerContent = async () => {
      if (!isMounted.current) return;
            
      try {
        setGroupedBannerLoading(true);
        setGroupedBannerError(null);
        
        const response = await getGroupedLatestBannerContent({ specialityIds });
        
        if (!isMounted.current) return;
        
        setGroupedBannerData(response);
        
        // Transform the response to desired format
        const transformedData = transformGroupedResponse(response);
        setTransformedBannerData(transformedData);
        
        // Extract content IDs for exclusion in latest content API
        const contentIds = transformedData.map((item: any) => item.id);
        
        // Call getLatest8Contents API with excluded IDs
        await fetchLatestContent(contentIds);
        
      } catch (error) {
        if (!isMounted.current) return;
        
        console.error("Error fetching grouped banner content:", error);
        setGroupedBannerError(error instanceof Error ? error.message : "Failed to fetch grouped banner content");
      } finally {
        if (isMounted.current) {
          setGroupedBannerLoading(false);
        }
      }
    };

    fetchGroupedBannerContent();
  }, [specialityIds]); // Only depend on specialityIds, not the entire pediaUserPreferences object

  // Function to fetch latest content with excluded IDs
  const fetchLatestContent = async (excludedIds: string[]) => {
    if (!isMounted.current) return;
        
    try {
      setLatestContentLoading(true);
      setLatestContentError(null);
      
      const response = await getLatest8Contents({ excludedIds, specialityIds });
      
      if (!isMounted.current) return;
      
      setLatestContentData(response);
      
      // Transform the response to desired format
      const transformedData = transformLatestContentResponse(response);
      setTransformedLatestData(transformedData);
      
    } catch (error) {
      if (!isMounted.current) return;
      
      console.error("Error fetching latest content:", error);
      setLatestContentError(error instanceof Error ? error.message : "Failed to fetch latest content");
    } finally {
      if (isMounted.current) {
        setLatestContentLoading(false);
      }
    }
  };

  // Set the active tab for Top Picks once available types are determined
  useEffect(() => {
    if (topPicksAvailableTypes.length > 0 && !activeTopPickTab) {
      const firstTabCode = topPicksAvailableTypes[0].code;
      setActiveTopPickTab(firstTabCode);
    }
  }, [topPicksAvailableTypes, activeTopPickTab]);

  // Render a loading state if filters have changed but API call is debounced
  if (!debouncedApiCall && !contentLoading && bannerItems.length === 0) {
    return <LoadingSkeleton />;
  }

  // Show error state if there's an API error
  if (contentError) {
    return <ErrorState />;
  }

  const hasNoContent = transformedBannerData.length === 0 && transformedLatestData.length === 0;
  const isAnyLoading = groupedBannerLoading || latestContentLoading;

  if (isAnyLoading) {
    return <LoadingSkeleton />;
  }

  if (hasNoContent && !isAnyLoading) {
    return (
      <div className="flex-1 overflow-auto pb-16 flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div className="bg-white rounded-2xl p-8 mx-4 text-center shadow-sm border border-gray-100 max-w-sm">
          <div className="mb-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
          <h3 className="text-gray-700 text-lg font-semibold mb-2">
            No content available in selected speciality
          </h3>
          <p className="text-gray-500 text-sm leading-relaxed">
            Featured and latest content will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto pb-16">
      <BannerSection
        items={transformedBannerData}
        loading={groupedBannerLoading}
        carouselRef={carouselRef}
        activeCardIndex={0}
        isPaused={isPaused}
        onCardClick={handleCardClick}
        setIsPaused={setIsPaused}
        extendedItems={[]}
        showEmptyState={!hasNoContent} // Only show individual empty state if consolidated one is not shown
      />

      <LatestSection 
        items={transformedLatestData} 
        loading={latestContentLoading} 
        showEmptyState={!hasNoContent} // Only show individual empty state if consolidated one is not shown
      />

      {/* <TopPicksSection
        items={topPicksItems}
        loading={contentLoading}
        availableTypes={topPicksAvailableTypes}
        activeTab={activeTopPickTab}
        onTabChange={setActiveTopPickTab}
        contentTypeHandler={contentTypeHandler}
      /> */}
    </div>
  );
};

export default ForYouSection;
